import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, User, Mail, Phone, MapPin, Calendar, Briefcase,
  Star, Award, Download, Edit, AlertCircle, Clock,
  Building, Target, Brain, Users, Filter, RefreshCw,
  Plus, ChevronDown, ChevronUp, Eye, Mail as MailIcon,
  Trash2, Shield // ✅ ADD: Import Trash2 and Shield icons
} from 'lucide-react';
import { candidatesApi } from '../services/api';
import CandidateDetailModal from '../components/recruiting/CandidateDetailModal';
import { useAuth } from '../contexts/AuthContext';
import Toast from '../components/ui/Toast';
// ✅ ADD: Import AppHeader and Pagination
import AppHeader from '../components/common/AppHeader';
import Pagination from '../components/ui/Pagination';
import useDebounce from '../hooks/useDebounce';
import websocketManager from '../services/websocketManager';

const Candidates = () => {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: 'updatedAt', direction: 'desc' });
  const [filters, setFilters] = useState({
    status: 'all',
    hasCvAnalysis: 'all',
    availability: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const candidateRequestGenerationRef = useRef(0);

  // ✅ ADD: Pagination state
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  // ✅ ADD: Debounced search for server-side filtering
  const debouncedSearch = useDebounce(searchValue, 300);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;
  
  // ✅ ADD: Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    candidate: null,
    loading: false
  });

  // ✅ ADD: Check if user is admin
  const isAdmin = user?.role === 'Admin';

  // Filter and sort candidates when data or filters change
  // NOTE: Search is now handled server-side, so we only apply client-side filters here
  useEffect(() => {
    let filtered = [...candidates];

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(candidate => {
        if (filters.status === 'with-needs') {
          return candidate.needs && candidate.needs.length > 0;
        } else if (filters.status === 'without-needs') {
          return !candidate.needs || candidate.needs.length === 0;
        }
        return true;
      });
    }

    // Apply CV analysis filter
    if (filters.hasCvAnalysis !== 'all') {
      filtered = filtered.filter(candidate => {
        const hasAnalysis = candidate.cvAnalysisStatus === 'READY';
        return filters.hasCvAnalysis === 'analyzed' ? hasAnalysis : !hasAnalysis;
      });
    }

    // Apply availability filter
    if (filters.availability !== 'all') {
      filtered = filtered.filter(candidate => {
        const availability = candidate.cvAvailability || candidate.cv_availability;
        if (filters.availability === 'available') {
          return availability && availability !== 'Non spécifiée' && availability !== 'Not specified';
        } else if (filters.availability === 'not-specified') {
          return !availability || availability === 'Non spécifiée' || availability === 'Not specified';
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      // Handle nested properties
      if (sortConfig.field === 'needs') {
        aValue = a.needs?.length || 0;
        bValue = b.needs?.length || 0;
      } else if (sortConfig.field === 'skills') {
        aValue = a.skills?.length || 0;
        bValue = b.skills?.length || 0;
      }

      // Handle date fields
      if (sortConfig.field === 'updatedAt' || sortConfig.field === 'createdAt') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredCandidates(filtered);
  }, [candidates, searchValue, filters, sortConfig]);

  const loadCandidates = useCallback(async (
    newPagination = paginationRef.current,
    search = debouncedSearchRef.current,
  ) => {
    const requestGeneration = ++candidateRequestGenerationRef.current;
    try {
      setLoading(true);

      // Build params with pagination and search
      const params = {
        limit: newPagination.limit,
        offset: newPagination.offset
      };

      // Add search if present
      if (search) {
        params.search = search;
      }

      const response = await candidatesApi.listCandidates(params);
      if (requestGeneration !== candidateRequestGenerationRef.current) return null;

      // Backend now returns { items, total, limit, offset }
      setCandidates(response.items || []);
      setPagination({
        limit: response.limit ?? newPagination.limit,
        offset: response.offset ?? newPagination.offset,
        total: response.total ?? 0
      });
      return response;
    } catch (error) {
      if (requestGeneration !== candidateRequestGenerationRef.current) return null;
      console.error('Error loading candidates:', error);
      setToast({
        message: 'Failed to load candidates',
        type: 'error'
      });
    } finally {
      if (requestGeneration === candidateRequestGenerationRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let timer;
    const unsubscribe = websocketManager.subscribe(message => {
      if (message.type !== 'connection_test' && ![
        'candidate_created', 'candidate_updated', 'candidate_deleted', 'candidate_merged',
        'step_created', 'step_updated', 'step_deleted',
      ].includes(message.event_type)) return;
      // Invalidate pending snapshots immediately, then coalesce event bursts.
      candidateRequestGenerationRef.current += 1;
      clearTimeout(timer);
      timer = setTimeout(() => loadCandidates(), 100);
    });
    return () => {
      unsubscribe();
      clearTimeout(timer);
      candidateRequestGenerationRef.current += 1;
    };
  }, [loadCandidates]);

  // ✅ ADD: Reload when debounced search changes (reset to first page)
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      const newPagination = { ...paginationRef.current, offset: 0 };
      setPagination(newPagination);
      loadCandidates(newPagination, debouncedSearch);
    }
  }, [debouncedSearch, loadCandidates]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadCandidates();
      setToast({
        message: 'Candidates refreshed successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error refreshing candidates:', error);
      setToast({
        message: 'Failed to refresh candidates',
        type: 'error'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  // ✅ PHASE 3: Wrap in useCallback for stable reference
  const openCandidateDetail = useCallback((candidate) => {
    setSelectedCandidate(candidate);
    setIsDetailModalOpen(true);
  }, []);

  // ✅ PHASE 3: Wrap in useCallback for stable reference
  const closeCandidateDetail = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedCandidate(null);
  }, []);

  // ✅ ADD: Delete candidate handlers
  const openDeleteConfirmation = (candidate) => {
    setDeleteConfirmation({
      isOpen: true,
      candidate,
      loading: false
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      candidate: null,
      loading: false
    });
  };

  const handleDeleteCandidate = async () => {
    if (!deleteConfirmation.candidate) return;

    try {
      setDeleteConfirmation(prev => ({ ...prev, loading: true }));
      
      await candidatesApi.deleteCandidate(deleteConfirmation.candidate.id);
      
      // Remove candidate from local state
      setCandidates(prev => prev.filter(c => c.id !== deleteConfirmation.candidate.id));
      
      setToast({
        message: `Candidate "${deleteConfirmation.candidate.name}" deleted successfully`,
        type: 'success'
      });
      
      closeDeleteConfirmation();
    } catch (error) {
      console.error('Error deleting candidate:', error);
      setToast({
        message: error.response?.data?.detail || 'Failed to delete candidate',
        type: 'error'
      });
    } finally {
      setDeleteConfirmation(prev => ({ ...prev, loading: false }));
    }
  };

  // ✅ PHASE 3: Wrap in useCallback for stable reference
  const handleDownloadCV = useCallback(async (candidate) => {
    if (!candidate.cvUrl) {
      setToast({
        message: 'No CV available for download',
        type: 'error'
      });
      return;
    }

    try {
      // Handle S3 URLs
      if (candidate.cvUrl.startsWith('s3://')) {
        // For S3 URLs, we need to get a presigned URL from the backend
        const response = await candidatesApi.getCandidate(candidate.id);
        const cvUrl = response.cvUrl;

        if (cvUrl && cvUrl.startsWith('http')) {
          window.open(cvUrl, '_blank');
        } else {
          setToast({
            message: 'CV download not available',
            type: 'error'
          });
        }
      } else if (candidate.cvUrl.startsWith('http')) {
        // Direct HTTP URL
        window.open(candidate.cvUrl, '_blank');
      } else {
        setToast({
          message: 'Invalid CV URL',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error downloading CV:', error);
      setToast({
        message: 'Failed to download CV',
        type: 'error'
      });
    }
  }, []);

  // ✅ PHASE 3: Stable callback for modal CV download
  const handleDownloadCandidateCV = useCallback(() => {
    if (selectedCandidate) {
      handleDownloadCV(selectedCandidate);
    }
  }, [selectedCandidate, handleDownloadCV]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSkills = (candidate) => {
    if (candidate.cvAnalysis?.hardskills) {
      return candidate.cvAnalysis.hardskills;
    }
    if (candidate.cvHardSkillsJson) {
      try {
        return JSON.parse(candidate.cvHardSkillsJson);
      } catch (e) {
        console.error('Error parsing cvHardSkillsJson:', e);
      }
    }
    return candidate.skills || [];
  };

  const SortableHeader = ({ field, children, className = '' }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center space-x-1 hover:text-gray-700 transition-colors ${className}`}
    >
      <span>{children}</span>
      {sortConfig.field === field ? (
        sortConfig.direction === 'asc' ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )
      ) : (
        <div className="w-3.5 h-3.5" />
      )}
    </button>
  );

  // ✅ ADD: Pagination handlers
  const handleNextPage = () => {
    const newPagination = {
      ...pagination,
      offset: pagination.offset + pagination.limit
    };
    setPagination(newPagination);
    loadCandidates(newPagination);
  };

  const handlePreviousPage = () => {
    const newPagination = {
      ...pagination,
      offset: Math.max(0, pagination.offset - pagination.limit)
    };
    setPagination(newPagination);
    loadCandidates(newPagination);
  };

  // ✅ ADD: Create actions for AppHeader
  const headerActions = useMemo(() => (
    <div className="flex items-center space-x-2">
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300 disabled:opacity-50"
      >
        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        <span>Refresh</span>
      </button>
      <button
        onClick={() => setShowFilters(!showFilters)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors border ${
          showFilters
            ? 'bg-blue-50 text-blue-700 border-blue-300'
            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border-gray-300'
        }`}
      >
        <Filter size={16} />
        <span>Filters</span>
      </button>
    </div>
  ), [refreshing, showFilters]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ MODIFY: Disable search bar in AppHeader */}
      <AppHeader
        showSearch={false}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showFilterButton={true}
        actions={headerActions}
      />

      {/* ✅ MODIFY: Add candidate-specific search bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Candidates</h1>
            <p className="text-gray-600 mt-1">
              Manage and search through all candidates in the system
            </p>
          </div>
          
          {/* ✅ ADD: Candidate search bar */}
          <div className="flex-1 max-w-md ml-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
              <input
                type="text"
                placeholder="Search candidates by name, email, role... (⌘K)"
                value={searchValue}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                aria-label="Search candidates"
              />
            </div>
          </div>
          
          {/* ✅ MODIFY: Move stats to the right */}
          <div className="flex items-center space-x-6 text-sm text-gray-600 ml-8">
            <div className="flex items-center space-x-2">
              <Users size={16} />
              <span>
                {pagination.offset + 1} - {Math.min(pagination.offset + filteredCandidates.length, pagination.total)} of {pagination.total} candidates
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Award size={16} />
              <span>{candidates.filter(c => c.cvAnalysisStatus === 'READY').length} analyzed (on page)</span>
            </div>
            {/* ✅ ADD: Admin indicator */}
            {isAdmin && (
              <div className="flex items-center space-x-2 text-orange-600">
                <Shield size={16} />
                <span>Admin Mode</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ ADD: Keyboard shortcut for candidate search */}
      {useEffect(() => {
        const handleKeyDown = (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            const searchInput = document.querySelector('input[placeholder*="Search candidates"]');
            if (searchInput) {
              searchInput.focus();
            }
          }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
      }, [])}

      {/* ✅ ADD: Advanced Filters section (moved from old search section) */}
      {showFilters && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label htmlFor="candidates-filter-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select id="candidates-filter-status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All candidates</option>
                <option value="with-needs">With needs</option>
                <option value="without-needs">Without needs</option>
              </select>
            </div>

            {/* CV Analysis Filter */}
            <div>
              <label htmlFor="candidates-filter-cv-analysis" className="block text-sm font-medium text-gray-700 mb-1">CV Analysis</label>
              <select id="candidates-filter-cv-analysis"
                value={filters.hasCvAnalysis}
                onChange={(e) => handleFilterChange('hasCvAnalysis', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="analyzed">Analyzed</option>
                <option value="not-analyzed">Not analyzed</option>
              </select>
            </div>

            {/* Availability Filter */}
            <div>
              <label htmlFor="candidates-filter-availability" className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
              <select id="candidates-filter-availability"
                value={filters.availability}
                onChange={(e) => handleFilterChange('availability', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="available">Available</option>
                <option value="not-specified">Not specified</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: 'all', hasCvAnalysis: 'all', availability: 'all' })}
                className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content - REPLACE TABLE WITH MOBILE CARDS */}
      <div className="px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading candidates...</span>
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchValue || Object.values(filters).some(f => f !== 'all') 
                ? 'No candidates found' 
                : 'No candidates yet'
              }
            </h3>
            <p className="text-gray-600">
              {searchValue || Object.values(filters).some(f => f !== 'all')
                ? 'Try adjusting your search or filters'
                : 'Candidates will appear here once they are added to the system'
              }
            </p>
          </div>
        ) : (
         // ✅ PHASE 3: Using pagination (50 items) instead of virtualization
         // Virtualization not needed with pagination - already efficient
         <div className="space-y-4">
           {filteredCandidates.map((candidate) => {
             const skills = getSkills(candidate);
             return (
               <div key={candidate.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                 {/* Header with avatar, name, and status */}
                 <div className="flex items-start justify-between mb-3">
                   <div className="flex items-center space-x-3 flex-1 min-w-0">
                     <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                       {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                     </div>
                     <div className="flex-1 min-w-0">
                       <h3 className="font-semibold text-gray-900 text-lg truncate">{candidate.name}</h3>
                       <p className="text-sm text-gray-600 truncate">{candidate.cvRole || candidate.cv_role || 'Not specified'}</p>
                       {candidate.email && (
                         <div className="flex items-center space-x-1 mt-1">
                           <MailIcon size={14} className="text-gray-400" />
                           <span className="text-sm text-gray-500 truncate">{candidate.email}</span>
                         </div>
                       )}
                     </div>
                   </div>
                   <div className="flex flex-col items-end space-y-2">
                     <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                       candidate.cvAnalysisStatus === 'READY'
                         ? 'bg-green-100 text-green-800'
                         : 'bg-yellow-100 text-yellow-800'
                     }`}>
                       {candidate.cvAnalysisStatus === 'READY' ? 'Analyzed' : 'Pending'}
                     </span>
                     {candidate.cvTrigramme && (
                       <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-mono">
                         {candidate.cvTrigramme}
                       </span>
                     )}
                   </div>
                 </div>

                 {/* Experience and needs info */}
                 <div className="grid grid-cols-2 gap-4 mb-3">
                   <div className="bg-gray-50 rounded-lg p-3">
                     <div className="flex items-center space-x-2 mb-1">
                       <Briefcase size={14} className="text-gray-500" />
                       <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Experience</span>
                     </div>
                     <p className="text-sm font-medium text-gray-900">
                       {candidate.cvExperienceYears || candidate.cv_experience_years || 'N/A'} years
                     </p>
                   </div>
                   <div className="bg-gray-50 rounded-lg p-3">
                     <div className="flex items-center space-x-2 mb-1">
                       <Target size={14} className="text-gray-500" />
                       <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Needs</span>
                     </div>
                     <p className="text-sm font-medium text-gray-900">
                       {candidate.needs?.length || 0} need{candidate.needs?.length !== 1 ? 's' : ''}
                     </p>
                     {candidate.needs?.length > 0 && (
                       <p className="text-xs text-gray-500 mt-1 truncate">
                         {candidate.needs.slice(0, 2).map(need => need.client).join(', ')}
                         {candidate.needs.length > 2 && '...'}
                       </p>
                     )}
                   </div>
                 </div>

                 {/* Skills section */}
                 {skills.length > 0 && (
                   <div className="mb-4">
                     <div className="flex items-center space-x-2 mb-2">
                       <Brain size={14} className="text-gray-500" />
                       <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Skills</span>
                     </div>
                     <div className="flex flex-wrap gap-2">
                       {skills.slice(0, 4).map((skill, index) => (
                         <span
                           key={index}
                           className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                         >
                           {skill}
                         </span>
                       ))}
                       {skills.length > 4 && (
                         <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                           +{skills.length - 4} more
                         </span>
                       )}
                     </div>
                   </div>
                 )}

                 {/* Footer with actions and last updated */}
                 <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                   <div className="text-xs text-gray-500">
                     Updated {formatDate(candidate.updatedAt)}
                   </div>
                   <div className="flex items-center space-x-1">
                     <button
                       onClick={() => openCandidateDetail(candidate)}
                       className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                       title="View details"
                       aria-label="View details"
                     >
                       <Eye size={18} />
                     </button>
                     {candidate.cvUrl && (
                       <button
                         onClick={() => handleDownloadCV(candidate)}
                         className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                         title="Download CV"
                         aria-label="Download CV"
                       >
                         <Download size={18} />
                       </button>
                     )}
                     {candidate.email && (
                       <a
                         href={`mailto:${candidate.email}`}
                         className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                         title="Send email"
                         aria-label="Send email"
                       >
                         <Mail size={18} />
                       </a>
                     )}
                     {isAdmin && (
                       <button
                         onClick={() => openDeleteConfirmation(candidate)}
                         className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                         title="Delete candidate (Admin only)"
                         aria-label="Delete candidate"
                       >
                         <Trash2 size={18} />
                       </button>
                     )}
                   </div>
                 </div>
               </div>
             );
           })}
         </div>
        )}

        {/* ✅ ADD: Pagination controls */}
        {!loading && filteredCandidates.length > 0 && (
          <Pagination
            limit={pagination.limit}
            offset={pagination.offset}
            total={pagination.total}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
          />
        )}
      </div>

      {/* Candidate Detail Modal - PHASE 3: Stable callbacks */}
      {selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          open={isDetailModalOpen}
          onClose={closeCandidateDetail}
          onDownloadCV={handleDownloadCandidateCV}
        />
      )}

      {/* ✅ ADD: Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && deleteConfirmation.candidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-candidate-title">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div>
                <h3 id="delete-candidate-title" className="text-lg font-semibold text-gray-900">Delete Candidate</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                Are you sure you want to permanently delete <strong>{deleteConfirmation.candidate.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">This will delete:</p>
                    <ul className="mt-1 space-y-1">
                      <li>• The candidate profile</li>
                      <li>• All associated steps and history</li>
                      <li>• CV analysis data</li>
                      <li>• All relationships with needs</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={closeDeleteConfirmation}
                disabled={deleteConfirmation.loading}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCandidate}
                disabled={deleteConfirmation.loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {deleteConfirmation.loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Delete Permanently</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Candidates;
